/**
 * Property tables are built by walking farms -> fields -> crop zones and
 * matching each zone against a flat list that lives on the task or
 * recommendation. Indexing that list once turns the inner search into a
 * lookup, and both views need the same two pieces.
 */

/**
 * Indexes a flat list by the crop zone id it refers to.
 *
 * Duplicate keys collapse to the last entry, which is the intended reading:
 * a crop zone appears once in a task or recommendation.
 *
 * @param {Array|undefined} items list carrying a crop zone reference
 * @param {(item: object) => string} getKey reads the crop zone id from an item
 * @returns {Map<string, object>}
 */
export const indexByCropZone = (items, getKey) =>
  new Map((items ?? []).map(item => [getKey(item), item]));

/**
 * The geoJSON features belonging to one field.
 *
 * @param {{features?: Array}|undefined} fieldsAndAreasGeoJSONCollection
 * @param {string} fieldId
 * @returns {Array|undefined} undefined when the collection carries no features
 */
export const getFieldFeatures = (fieldsAndAreasGeoJSONCollection, fieldId) =>
  fieldsAndAreasGeoJSONCollection?.features?.filter(
    feature => fieldId === feature.properties.id
  );

/**
 * One table row per crop zone the recommendation covers.
 *
 * The two views that show these rows disagree on the coverage figure -- one
 * rounds it to a percentage, the other defaults it pending an API change --
 * so that field is supplied by the caller rather than decided here.
 *
 * @param {{properties?: Array}|undefined} propertyData farms, fields and zones
 * @param {{cropZones?: Array}|undefined} recommendation covered zones
 * @param {{features?: Array}|undefined} fieldsAndAreasGeoJSONCollection
 * @param {(cropZone: object) => object} getCoverageFields merged into each row
 * @returns {Array<object>} rows, empty when nothing matches
 */
export const buildRecommendationRows = (
  propertyData,
  recommendation,
  fieldsAndAreasGeoJSONCollection,
  getCoverageFields
) => {
  const recommendationCropZones = indexByCropZone(
    recommendation?.cropZones,
    cropZone => cropZone.cropZoneIdValue
  );

  return (propertyData?.properties ?? []).flatMap(property =>
    (property?.fields ?? []).flatMap(field =>
      (field?.cropzones ?? []).flatMap(zone => {
        const recommendationCropZone = recommendationCropZones.get(zone.id);
        if (!recommendationCropZone) {
          return [];
        }

        return [
          {
            farmId: property.id,
            cropZoneId: zone.id,
            fieldName: field.name,
            cropZoneName: zone.name,
            cropName: zone.crop?.name,
            areaValue: recommendationCropZone.areaValue,
            areaUnit: recommendationCropZone.areaUnit,
            boundaryArea: zone.boundaryArea,
            reportedArea: zone.reportedArea,
            ...getCoverageFields(recommendationCropZone),
            features: getFieldFeatures(
              fieldsAndAreasGeoJSONCollection,
              field.id
            )
          }
        ];
      })
    )
  );
};

/**
 * One table row per crop zone the task covers.
 *
 * Walks the property tree and keeps the zones the task refers to, reading the
 * areas and coverage from the task rather than the zone.
 *
 * @param {{properties?: Array}|undefined} propertyData farms, fields and zones
 * @param {{properties?: Array}|undefined} task carries the covered zones
 * @param {{features?: Array}|undefined} fieldsAndAreasGeoJSONCollection
 * @returns {Array<object>} rows, empty when nothing matches
 */
export const buildTaskRows = (
  propertyData,
  task,
  fieldsAndAreasGeoJSONCollection
) => {
  const taskProperties = indexByCropZone(
    task?.properties,
    property => property?.cropZoneId
  );

  return (propertyData?.properties ?? []).flatMap(property =>
    (property?.fields ?? []).flatMap(field =>
      (field?.cropzones ?? []).flatMap(zone => {
        const taskProperty = taskProperties.get(zone?.id);
        if (!taskProperty) {
          return [];
        }

        return [
          {
            farmId: taskProperty?.farmId,
            cropZoneId: zone?.id,
            fieldName: field?.name,
            fieldId: field?.id,
            cropZoneName: zone?.name,
            cropName: zone?.crop?.name,
            areaValue: taskProperty?.areaValue,
            areaUnit: taskProperty?.areaUnit,
            boundaryArea: zone?.boundaryArea,
            reportedArea: zone?.reportedArea,
            coveragePercent: taskProperty?.coveragePct,
            features: getFieldFeatures(
              fieldsAndAreasGeoJSONCollection,
              field?.id
            )
          }
        ];
      })
    )
  );
};
